import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Modal, Icon } from 'semantic-ui-react';
import AddToCartButton from './components/AddToCartButton';
import QuantitySelector from './components/QuantitySelector';
import VariationSelection from './components/VariationSelection';
import ModifierSetSelection from './components/ModifierSetSelection';
import SpecialInstructions from './components/SpecialInstructions';
import './MenuItemCartModal.css';

class MenuItemCartModal extends React.Component {
  constructor(props) {
    super(props);

    const {
      variations,
      initialSelectedVariation,
      initialSelectedModifiers,
      initialSpecialInstructions,
      initialQuantity,
    } = props;

    this.state = {
      selectedVariation:
        Object.keys(initialSelectedVariation).length > 0
          ? initialSelectedVariation
          : variations.length === 1
          ? variations[0]
          : {},
      selectedModifiers: initialSelectedModifiers.length > 0 ? initialSelectedModifiers : [],
      specialInstructions: initialSpecialInstructions.length > 0 ? initialSpecialInstructions : '',
      quantity: initialQuantity !== 0 ? initialQuantity : 1,
    };

    this.onSelectVariation = this.onSelectVariation.bind(this);
    this.onSelectModifier = this.onSelectModifier.bind(this);
    this.isModifierSelected = this.isModifierSelected.bind(this);
    this.isModifierDisabled = this.isModifierDisabled.bind(this);
    this.onSpecialInstructionsChange = this.onSpecialInstructionsChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onAddToCart = this.onAddToCart.bind(this);
    this.calculateTotal = this.calculateTotal.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  onSelectVariation(variationIndex) {
    const { variations } = this.props;

    this.setState({ selectedVariation: variations[variationIndex] });
  }

  onSelectModifier(modifier, modifierSet) {
    const { selectedModifiers } = this.state;

    const finalModifier = {
      ...modifier,
      modifierSetId: modifierSet._id,
    };

    // If the modifier already exists in the selection, remove it
    if (this.isModifierSelected(finalModifier._id)) {
      const indexOfModifier = selectedModifiers.map(({ _id }) => _id).indexOf(finalModifier._id);

      this.setState({
        selectedModifiers: [
          ...selectedModifiers.slice(0, indexOfModifier),
          ...selectedModifiers.slice(indexOfModifier + 1, selectedModifiers.length),
        ],
      });
    } else {
      this.setState({ selectedModifiers: [...selectedModifiers, finalModifier] });
    }
  }

  isModifierSelected(modifierId) {
    const { selectedModifiers } = this.state;

    return selectedModifiers.map(({ _id }) => _id).includes(modifierId);
  }

  onSpecialInstructionsChange(e, { value }) {
    this.setState({ specialInstructions: value });
  }

  onQuantityChange(deltaQuantity) {
    this.setState(({ quantity }) => ({ quantity: quantity + deltaQuantity }));
  }

  onAddToCart() {
    const { cart, item, onClose, type } = this.props;
    const { selectedVariation, selectedModifiers, specialInstructions, quantity } = this.state;
    const { addItem, updateItem, getItemIndex } = cart;

    if (type === 'add') {
      addItem({
        ...item,
        selectedVariation,
        selectedModifiers,
        specialInstructions,
        quantity,
        cartId: _.uniqueId('cart'),
      });
      this.resetState();
    } else if (type === 'update') {
      const cartItemIndex = getItemIndex(item, ({ cartId }) => cartId);

      updateItem(
        {
          ...item,
          selectedVariation,
          selectedModifiers,
          specialInstructions,
          quantity,
        },
        cartItemIndex,
      );
    }

    onClose();
  }

  calculateTotal() {
    const { selectedVariation, selectedModifiers, quantity } = this.state;

    const { price: variationPrice = 0.0 } = selectedVariation;
    const modifierPrices = selectedModifiers.map(({ price }) => price);
    const modifiersTotal = modifierPrices.reduce((total, nextPrice) => total + nextPrice, 0.0);
    const total = modifiersTotal + variationPrice;

    return total * quantity;
  }

  resetState() {
    const { variations } = this.props;

    this.setState({
      selectedVariation: variations.length === 1 ? variations[0] : {},
      selectedModifiers: [],
      specialInstructions: '',
      quantity: 1,
    });
  }

  isModifierDisabled(modifierSet, modifierId) {
    const { selectedModifiers } = this.state;
    const { max_allowed, id } = modifierSet;

    const selectedModifierSetModifiers = selectedModifiers
      .filter(({ modifierSetId }) => modifierSetId === id)
      .map(({ id }) => id);

    return (
      max_allowed > 0 &&
      selectedModifierSetModifiers.length === max_allowed &&
      !selectedModifierSetModifiers.includes(modifierId)
    );
  }

  addToCartText() {
    const {
      cart: { isOpen },
      type,
    } = this.props;
    if (!isOpen) return 'Ordering closed';
    if (type === 'add') {
      return 'Add to Cart';
    }
    return 'Update Item';
  }

  addToCartDisabled() {
    const {
      modifierSets,
      cart: { isOpen },
    } = this.props;
    const { selectedVariation, selectedModifiers } = this.state;

    const requiredModifierSets = modifierSets.filter(({ min_allowed }) => min_allowed > 0);

    const modifierSetsSatisfied = requiredModifierSets
      .map(({ id, min_allowed }) => {
        const selectedModifierSetModifiers = selectedModifiers.filter(
          ({ modifierSetId }) => modifierSetId === id,
        );
        return selectedModifierSetModifiers.length >= min_allowed;
      })
      .every(val => val);

    return !modifierSetsSatisfied || Object.keys(selectedVariation).length === 0;
  }

  render() {
    const {
      open,
      onClose,
      trigger,
      item,
      variations = [],
      modifierSets,
      modifiers,
      cart: { isOpen },
    } = this.props;
    const { selectedVariation, specialInstructions, quantity } = this.state;
    let { name, description = '' } = item;

    description = description || '';

    return (
      <Modal
        className="item-cart-modal"
        open={open}
        onClose={onClose}
        trigger={trigger}
        dimmer="inverted"
      >
        <Modal.Header>
          <div>{name}</div>
          <div>
            <Icon name="cancel" size="large" onClick={onClose} />
          </div>
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {description && description.length > 0 && <p>{description}</p>}
            {variations && variations.length > 1 && (
              <VariationSelection
                showDivider={description.length > 0}
                variations={variations}
                onSelectVariation={this.onSelectVariation}
                selectedVariation={selectedVariation}
                checkoutDisabled={!isOpen}
              />
            )}
            {modifierSets.map((ms, index) => (
              <ModifierSetSelection
                key={ms.name}
                showDivider={index !== 0 || variations.length > 1 || description.length > 0}
                onSelectModifier={this.onSelectModifier}
                isModifierSelected={this.isModifierSelected}
                allModifiers={modifiers}
                modifierSets={modifierSets}
                isModifierDisabled={this.isModifierDisabled}
                checkoutDisabled={!isOpen}
                {...ms}
              />
            ))}
            <SpecialInstructions
              showDivider={
                variations.length > 1 || description.length > 0 || modifierSets.length > 0
              }
              specialInstructions={specialInstructions}
              onSpecialInstructionsChange={this.onSpecialInstructionsChange}
              checkoutDisabled={!isOpen}
            />
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <QuantitySelector
            onQuantityChange={this.onQuantityChange}
            quantity={quantity}
            checkoutDisabled={!isOpen}
          />
          <AddToCartButton
            addToCartText={this.addToCartText()}
            checkoutDisabled={!isOpen}
            disabled={this.addToCartDisabled()}
            onAddToCart={this.onAddToCart}
            totalPrice={this.calculateTotal()}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

export default MenuItemCartModal;

MenuItemCartModal.propTypes = {
  trigger: PropTypes.element,
  type: PropTypes.oneOf(['add', 'update']),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  item: PropTypes.object,
  modifierSets: PropTypes.array,
  modifiers: PropTypes.array,
  variations: PropTypes.array,
  cart: PropTypes.object,
  initialSelectedVariation: PropTypes.object,
  initialSelectedModifiers: PropTypes.array,
  initialSpecialInstructions: PropTypes.string,
  initialQuantity: PropTypes.number,
};

MenuItemCartModal.defaultProps = {
  trigger: <div />,
  type: 'add',
  open: false,
  onClose: () => null,
  item: {},
  modifierSets: [],
  modifiers: [],
  variations: [],
  cart: {},
  initialSelectedVariation: {},
  initialSelectedModifiers: [],
  initialSpecialInstructions: '',
  initialQuantity: 0,
};
